/* Progressive UI only. Product forms, pricing, variants and cart requests remain native Dawn. */
(() => {
  // Progressive policy navigation. Shopify remains the sole source of the policy wording.
  class PhPolicy extends HTMLElement {
    connectedCallback() {
      const body = this.querySelector('.shopify-policy__body');
      const index = this.querySelector('.ph-policy-index');
      if (!body || !index || this.dataset.initialized) return;
      this.dataset.initialized = 'true';
      const title = document.getElementById('ph-policy-title')?.textContent.trim().toLowerCase();
      body.querySelectorAll('h1').forEach((heading) => {
        if (heading.textContent.trim().toLowerCase() === title) heading.hidden = true;
      });
      const headings = Array.from(body.querySelectorAll('h2')).filter((heading) => heading.textContent.trim());
      const list = index.querySelector('ol');
      headings.forEach((heading, position) => {
        if (!heading.id) {
          const slug = heading.textContent.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
          let id = `ph-policy-${slug || position + 1}`;
          while (document.getElementById(id)) id += `-${position + 1}`;
          heading.id = id;
        }
        heading.tabIndex = -1;
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.setAttribute('href', `#${heading.id}`);
        link.textContent = heading.textContent.trim();
        item.append(link);
        list.append(item);
      });
      index.hidden = headings.length < 2;
      // Style the existing date in place; never infer a new date from the current day.
      const date = Array.from(body.querySelectorAll('p')).find((paragraph) => /^(effective date|last updated|last revised)\s*:/i.test(paragraph.textContent.trim()));
      date?.classList.add('ph-policy-date');
      const current = this.querySelector('.ph-policy-nav [aria-current="page"]');
      const navList = current?.closest('ul');
      if (current && navList && navList.scrollWidth > navList.clientWidth) {
        navList.scrollLeft = current.offsetLeft - navList.offsetLeft - 20;
      }
      // Native anchors handle navigation and keyboard focus. Restore a direct section link
      // after its generated target becomes available on a fresh page load.
      if (location.hash.startsWith('#ph-policy-') && location.hash !== '#ph-policy-title') {
        const target = headings.find((heading) => `#${heading.id}` === location.hash);
        if (target) requestAnimationFrame(() => target.scrollIntoView());
      }
    }
  }

  // Keep the manufacturer's original mark. Retry a failed asset once with a fresh URL;
  // if delivery still fails, show the full name instead of the browser's clipped alt text.
  class PhBrands extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const { signal } = this.events;
      this.querySelectorAll('.ph-brand-logo').forEach((logo) => {
        const fallback = logo.parentElement.querySelector('.ph-brand-fallback');
        const showLogo = () => {
          logo.hidden = false;
          if (fallback) fallback.hidden = true;
        };
        const recover = () => {
          logo.hidden = true;
          if (fallback) fallback.hidden = false;
          if (logo.dataset.phRetried) return;
          logo.dataset.phRetried = 'true';
          const retry = new URL(logo.currentSrc || logo.src, document.baseURI);
          retry.searchParams.set('ph_logo_retry', '1');
          logo.removeAttribute('srcset');
          logo.loading = 'eager';
          logo.src = retry.href;
        };
        logo.addEventListener('load', showLogo, { signal });
        logo.addEventListener('error', recover, { signal });
        if (logo.complete && !logo.naturalWidth) recover();
      });
    }
    disconnectedCallback() { this.events?.abort(); }
  }

  class PhHeader extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const { signal } = this.events;
      this.dialog = this.querySelector('dialog');
      this.opener = this.querySelector('[data-ph-menu-open]');
      if (this.dialog?.showModal && this.opener) {
        this.opener.hidden = false;
        this.opener.addEventListener('click', () => {
          delete this.dialog.dataset.closing;
          this.dialog.showModal();
          this.dialog.scrollTop = 0;
          // Focus the dialog itself rather than its first control, so opening the menu does
          // not paint a focus ring on the logo or the close button. Escape and the focus
          // trap still work, and Tab moves to the first link from here.
          this.dialog.focus({ preventScroll: true });
          this.opener.setAttribute('aria-expanded', 'true');
          document.body.classList.add('ph-menu-open-body');
        }, { signal });
        this.querySelector('[data-ph-menu-close]')?.addEventListener('click', () => this.closeDialog(), { signal });
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog && event.clientX > this.dialog.getBoundingClientRect().right) this.closeDialog();
          if (event.target.closest('a[href^="#"]')) this.closeDialog();
        }, { signal });
        // Esc fires cancel; route it through the exit animation instead of the instant native close.
        this.dialog.addEventListener('cancel', (event) => {
          if (this.dialog.dataset.closing) return;
          event.preventDefault();
          this.closeDialog();
        }, { signal });
        this.dialog.addEventListener('close', () => {
          delete this.dialog.dataset.closing;
          this.opener.setAttribute('aria-expanded', 'false');
          document.body.classList.remove('ph-menu-open-body');
          this.opener.focus({ preventScroll: true });
        }, { signal });
      }
      this.querySelectorAll('[data-ph-nav]').forEach((details) => {
        const summary = details.querySelector(':scope > summary');
        if (!summary) return;
        summary.setAttribute('aria-expanded', String(details.open));
        details.addEventListener('toggle', () => {
          summary.setAttribute('aria-expanded', String(details.open));
          if (details.open) details.parentElement.querySelectorAll(':scope > details[open]').forEach((other) => {
            if (other !== details) other.open = false;
          });
        }, { signal });
        details.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && details.open) {
            event.preventDefault();
            event.stopPropagation();
            details.open = false;
            summary.focus();
          }
        }, { signal });
      });
      const closeDesktopMenus = () => this.querySelectorAll('.ph-desktop-nav details[open]').forEach((details) => { details.open = false; });
      // Desktop mega menus open on hover intent; click/keyboard toggling still works for touch and AT users.
      // Bound once the viewport is desktop-sized, including after a resize up from mobile.
      const desktop = window.matchMedia('(min-width: 990px)');
      const bindHoverIntent = () => {
        if (this.hoverBound || !desktop.matches) return;
        this.hoverBound = true;
        this.querySelectorAll('.ph-desktop-nav details[data-ph-nav]').forEach((details) => {
          let leaveTimer;
          details.addEventListener('pointerenter', (event) => {
            if (event.pointerType !== 'mouse' || !desktop.matches) return;
            clearTimeout(leaveTimer);
            if (!details.open) details.open = true;
          }, { signal });
          details.addEventListener('pointerleave', (event) => {
            if (event.pointerType !== 'mouse' || !desktop.matches) return;
            leaveTimer = setTimeout(() => { details.open = false; }, 180);
          }, { signal });
          // Hover already opened the panel, so a mouse click on a collection label goes to the collection.
          // Touch and keyboard keep the native toggle, since that is how they open the panel.
          const summary = details.querySelector(':scope > summary[data-ph-href]');
          if (!summary) return;
          let pointer = '';
          summary.addEventListener('pointerdown', (event) => { pointer = event.pointerType; }, { signal });
          summary.addEventListener('click', (event) => {
            const fromMouse = pointer === 'mouse' && event.detail > 0;
            pointer = '';
            if (!fromMouse || !desktop.matches) return;
            event.preventDefault();
            if (event.metaKey || event.ctrlKey) window.open(summary.dataset.phHref, '_blank', 'noopener');
            else window.location.href = summary.dataset.phHref;
          }, { signal });
        });
      };
      bindHoverIntent();
      desktop.addEventListener('change', bindHoverIntent, { signal });
      document.addEventListener('click', (event) => { if (!this.contains(event.target)) closeDesktopMenus(); }, { signal });
      // Close only when focus moves to another element outside the header (keyboard Tab). A tap briefly
      // drops focus to the body before its click fires, and closing then would swallow taps on panel links.
      this.addEventListener('focusout', (event) => { if (event.relatedTarget && !this.contains(event.relatedTarget)) closeDesktopMenus(); }, { signal });
      this.resize = new ResizeObserver(() => {
        document.documentElement.style.setProperty('--ph-header-height', `${Math.ceil(this.getBoundingClientRect().height)}px`);
        if (window.innerWidth >= 990 && this.dialog?.open) this.dialog.close();
      });
      this.resize.observe(this);
      // Reattach the native cart trigger after a Theme Editor header replacement.
      const drawer = document.querySelector('cart-drawer');
      if (window.Shopify?.designMode && drawer?.setHeaderCartIconAccessibility) drawer.setHeaderCartIconAccessibility();
    }
    closeDialog() {
      const dialog = this.dialog;
      if (!dialog?.open || dialog.dataset.closing) return;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced || !dialog.animate) { dialog.close(); return; }
      dialog.dataset.closing = '1';
      let done = false;
      const finish = () => { if (done) return; done = true; if (dialog.open) dialog.close(); };
      dialog.addEventListener('animationend', finish, { once: true });
      setTimeout(finish, 320);
    }
    disconnectedCallback() {
      this.events?.abort();
      this.resize?.disconnect();
      if (this.dialog?.open) document.body.classList.remove('ph-menu-open-body');
    }
  }

  class PhStickyAtc extends HTMLElement {
    connectedCallback() {
      this.events = new AbortController();
      const { signal } = this.events;
      this.productInfo = document.getElementById(`MainProduct-${this.dataset.section}`);
      this.stickyButton = this.querySelector('[data-ph-sticky-button]');
      this.price = this.querySelector('.ph-sticky-price');
      if (!this.productInfo || !this.stickyButton || !this.price) return;
      this.stickyButton.addEventListener('click', () => {
        const { button, form } = this.mainControls();
        if (!button || !form || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
        if (!form.reportValidity()) { this.setVisible(false); return; }
        form.requestSubmit(button);
      }, { signal });
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = 0; this.sync(); });
      };
      this.observer = new MutationObserver(this.schedule);
      this.observer.observe(this.productInfo, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'hidden', 'class', 'value'] });
      // Observe overlay state only, not unrelated page subtree mutations.
      this.overlayObserver = new MutationObserver(this.schedule);
      document.querySelectorAll('dialog, cart-drawer, details-modal details').forEach((overlay) => {
        this.overlayObserver.observe(overlay, { attributes: true, attributeFilter: ['open', 'class'] });
      });
      window.addEventListener('scroll', this.schedule, { passive: true, signal });
      window.addEventListener('resize', this.schedule, { passive: true, signal });
      document.addEventListener('focusin', this.schedule, { signal });
      document.addEventListener('focusout', this.schedule, { signal });
      this.productInfo.addEventListener('change', this.schedule, { signal });
      window.visualViewport?.addEventListener('resize', this.schedule, { signal });
      this.sync();
    }
    mainControls() {
      return {
        button: this.productInfo?.querySelector('product-form [name="add"]'),
        form: this.productInfo?.querySelector('product-form form'),
      };
    }
    sync() {
      const { button, form } = this.mainControls();
      if (!button || !form) return this.setVisible(false);
      const price = this.productInfo.querySelector(`[id="price-${this.dataset.section}"]`);
      const priceHTML = price && !price.classList.contains('hidden') ? price.innerHTML : '';
      if (this.price.innerHTML !== priceHTML) this.price.innerHTML = priceHTML;
      const label = button.querySelector('span')?.textContent.trim() || button.textContent.trim();
      if (this.stickyButton.querySelector('span').textContent !== label) this.stickyButton.querySelector('span').textContent = label;
      const unavailable = button.disabled || button.getAttribute('aria-disabled') === 'true' || !form.querySelector('[name="id"]')?.value;
      this.stickyButton.disabled = unavailable;
      this.stickyButton.setAttribute('aria-busy', String(button.classList.contains('loading')));
      const error = this.productInfo.querySelector('.product-form__error-message-wrapper:not([hidden])');
      const errorText = error?.textContent.trim() || '';
      if (errorText && errorText !== this.lastError) {
        this.lastError = errorText;
        this.setVisible(false);
        error.scrollIntoView({ block: 'center', behavior: 'auto' });
        button.focus({ preventScroll: true });
        return;
      }
      if (!errorText) this.lastError = '';
      const focus = document.activeElement;
      const editing = focus?.matches('input,textarea,select,[contenteditable="true"]');
      const keyboard = window.visualViewport && window.visualViewport.height < window.innerHeight * 0.75;
      const overlay = document.querySelector('dialog[open],cart-drawer.active,details-modal details[open]');
      // The bar activates only after scrolling past the original buy button, not while approaching it.
      const pastButton = button.getBoundingClientRect().bottom < 0;
      this.setVisible(window.innerWidth < 990 && pastButton && !editing && !keyboard && !overlay);
    }
    setVisible(visible) {
      if (visible === this.visible) return;
      this.visible = visible;
      clearTimeout(this.hideTimer);
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (visible) {
        this.hidden = false;
        if (reduced) delete this.dataset.offscreen;
        else {
          this.dataset.offscreen = '1';
          requestAnimationFrame(() => requestAnimationFrame(() => { if (this.visible) delete this.dataset.offscreen; }));
        }
        document.documentElement.style.setProperty('--ph-sticky-height', `${Math.ceil(this.getBoundingClientRect().height)}px`);
      } else {
        document.documentElement.style.setProperty('--ph-sticky-height', '0px');
        if (reduced) { this.hidden = true; return; }
        this.dataset.offscreen = '1';
        this.hideTimer = setTimeout(() => { if (!this.visible) this.hidden = true; }, 220);
      }
    }
    disconnectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
      this.overlayObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      clearTimeout(this.hideTimer);
      document.documentElement.style.setProperty('--ph-sticky-height', '0px');
    }
  }

  class PhProductDetails extends HTMLElement {
    connectedCallback() {
      this.fallback = this.querySelector('.ph-native-description');
      this.app = this.querySelector('.ph-app-details');
      if (!this.fallback || !this.app) return;
      // Preserve the server-rendered description unless Tabs Studio has rendered that same content.
      this.originalText = this.fallback.querySelector('.rte')?.textContent.replace(/\s+/g, ' ').trim() || '';
      this.check = () => {
        // innerText skips content the app has collapsed or hidden, so a Tabs Studio block
        // that holds the description but never lays out its tabs keeps the fallback visible.
        const normalized = this.app.innerText.replace(/\s+/g, ' ').trim();
        const rendered = this.app.querySelector('[role="tabpanel"],.station-tabs-content,.station-tabs-tabcontent,.station-tabs');
        const appVisible = rendered && !rendered.closest('[hidden]') && getComputedStyle(rendered).display !== 'none' && getComputedStyle(rendered).visibility !== 'hidden';
        const ownsDescription = appVisible && this.originalText.length > 0 && normalized.includes(this.originalText.slice(0, 160));
        this.fallback.hidden = !!ownsDescription;
        // The jump list and expander belong to the native copy, so they go when the app owns it.
        if (this.nav) this.nav.hidden = !!ownsDescription || !(this.navCount >= 2);
        if (ownsDescription && this.body) {
          this.body.classList.remove('is-collapsed');
          if (this.more) this.more.hidden = true;
        }
      };
      this.observer = new MutationObserver(this.check);
      this.observer.observe(this.app, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden'] });
      this.buildNav();
      this.check();
      this.setupCollapse();
    }
    // Jump list built from the description's own headings. Bold-only paragraphs count as
    // headings too because much of the supplier copy marks sections that way; they get a
    // class so the CSS can style them like real headings.
    buildNav() {
      this.nav = this.querySelector('.ph-details-nav');
      const list = this.nav?.querySelector('ul');
      const rte = this.fallback.querySelector('.rte');
      if (!list || !rte) return;
      const used = new Set();
      const items = [];
      // Jump-list label only: joins spaced-out capitals ("D E T A I L S") and title-cases
      // all-caps lines. The description text itself is left exactly as the catalog has it.
      const label = (raw) => {
        let text = raw.replace(/ /g, ' ').trim();
        const tokens = text.split(/\s+/);
        if (tokens.length >= 3 && tokens.every((token) => token.length === 1)) {
          text = text.split(/\s{2,}/).map((word) => word.replace(/\s/g, '')).join(' ');
        }
        return text.replace(/\s+/g, ' ').replace(/\s*\|\s*/g, ' | ').replace(/:$/, '').trim();
      };
      const found = [];
      rte.querySelectorAll('h1,h2,h3,h4,p,div').forEach((node) => {
        let target = node;
        let level = parseInt(node.tagName.slice(1), 10) || 5;
        if (!/^H[1-4]$/.test(node.tagName)) {
          // A paragraph that is only a bold run, or opens with a bold run on its own line.
          const children = Array.from(node.childNodes).filter((child) => !(child.nodeType === 3 && !child.textContent.trim()));
          const lead = children[0];
          if (!lead || lead.nodeType !== 1 || !/^(STRONG|B)$/.test(lead.tagName)) return;
          const alone = children.length === 1 || (children.length >= 2 && children[1].nodeType === 1 && children[1].tagName === 'BR');
          if (!alone) return;
          target = children.length === 1 ? node : lead;
        }
        const text = target.textContent.replace(/\s+/g, ' ').trim();
        if (!text || text.length > 60) return;
        if (target !== node || level === 5) target.classList.add('ph-details-subhead');
        found.push({ target, level, name: label(target.textContent) });
      });
      // When the copy has two or more top-level headings, list only those so the jump
      // list reads as the major sections rather than every feature callout beneath them.
      const top = Math.min(...found.map((entry) => entry.level));
      const majors = found.filter((entry) => entry.level === top);
      (majors.length >= 2 ? majors : found).slice(0, 8).forEach(({ target, name }) => {
        let id = `ph-detail-${name.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || items.length + 1}`;
        while (used.has(id) || document.getElementById(id)) id += `-${items.length + 1}`;
        used.add(id);
        target.id = id;
        items.push({ id, text: name });
      });
      this.navCount = items.length;
      if (items.length < 2) return;
      items.forEach(({ id, text }) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${id}`;
        link.textContent = text;
        item.append(link);
        list.append(item);
      });
      list.addEventListener('click', (event) => {
        if (event.target.closest('a')) this.expand();
      });
    }
    // Long descriptions are trimmed to the configured height with a button to expand.
    // The measurement re-runs as images load; once the shopper expands it stays open.
    setupCollapse() {
      this.body = this.querySelector('.ph-details-body');
      this.content = this.querySelector('.ph-details-content');
      this.more = this.querySelector('.ph-details-more');
      const limit = parseInt(this.dataset.collapse, 10) || 0;
      if (!this.body || !this.content || !this.more || !limit) return;
      this.more.addEventListener('click', () => (this.expanded ? this.collapse() : this.expand()));
      this.measure = () => {
        if (this.expanded || this.fallback.hidden) return;
        const tall = this.content.scrollHeight > limit + 160;
        this.body.classList.toggle('is-collapsed', tall);
        this.body.style.setProperty('--ph-details-collapse', `${limit}px`);
        this.more.hidden = !tall;
        this.more.setAttribute('aria-expanded', String(!tall));
      };
      this.resize = new ResizeObserver(this.measure);
      this.resize.observe(this.content);
      this.measure();
      const target = location.hash && location.hash.length > 1 && document.getElementById(location.hash.slice(1));
      if (target && this.content.contains(target)) this.expand();
    }
    expand() {
      if (!this.body || this.expanded) return;
      this.expanded = true;
      this.body.classList.remove('is-collapsed');
      this.more.querySelector('span').textContent = this.dataset.less;
      this.more.setAttribute('aria-expanded', 'true');
      this.more.classList.add('is-open');
    }
    collapse() {
      if (!this.body) return;
      this.expanded = false;
      this.body.classList.add('is-collapsed');
      this.more.querySelector('span').textContent = this.dataset.more;
      this.more.setAttribute('aria-expanded', 'false');
      this.more.classList.remove('is-open');
      this.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
    disconnectedCallback() { this.observer?.disconnect(); this.resize?.disconnect(); }
  }
  // Featured equipment tabs. Progressive: panels are server-rendered and only the
  // non-active ones are hidden, so with JS off every panel stays reachable in the DOM.
  class PhFeaturedTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-ph-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-ph-panel]'));
      if (this.tabs.length < 2) return;
      this.events?.abort();
      this.events = new AbortController();
      const { signal } = this.events;
      this.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-ph-tab]');
        if (tab) this.select(tab);
      }, { signal });
      this.addEventListener('keydown', (event) => {
        const tab = event.target.closest('[data-ph-tab]');
        if (!tab) return;
        const keys = { ArrowRight: 1, ArrowLeft: -1 };
        if (event.key in keys) {
          event.preventDefault();
          const index = this.tabs.indexOf(tab);
          const next = this.tabs[(index + keys[event.key] + this.tabs.length) % this.tabs.length];
          this.select(next);
          next.focus();
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          const next = event.key === 'Home' ? this.tabs[0] : this.tabs[this.tabs.length - 1];
          this.select(next);
          next.focus();
        }
      }, { signal });
    }
    disconnectedCallback() { this.events?.abort(); }
    select(tab) {
      const id = tab.dataset.phTab;
      this.tabs.forEach((item) => {
        const active = item === tab;
        item.setAttribute('aria-selected', active ? 'true' : 'false');
        item.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.phPanel !== id;
      });
    }
  }

  // FAQ accordion. Progressive: native <details>/<summary> works without JS and keeps
  // its keyboard behaviour; this only animates the answer's height on open/close.
  class PhFaq extends HTMLElement {
    connectedCallback() {
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      const first = this.querySelector('.ph-faq-item > .rte');
      if (first) this.padding(first);
      this.events?.abort();
      this.events = new AbortController();
      this.addEventListener('click', (event) => {
        const summary = event.target.closest('summary');
        if (!summary || !this.contains(summary)) return;
        const item = summary.parentElement;
        const body = item.querySelector(':scope > .rte');
        if (!body || !body.animate || this.reduced.matches) return;
        event.preventDefault();
        this.toggle(item, body);
      }, { signal: this.events.signal });
    }
    disconnectedCallback() { this.events?.abort(); }
    // A click mid-animation retargets from the current height instead of being swallowed.
    // The answer is border-box with padding-bottom, so height alone cannot reach 0: it clamps
    // at the padding and the tail of the timing function plays out against a frozen value,
    // which reads as lag. Animate the padding shut as well and measure the open height from
    // the live box so both directions travel the full distance.
    toggle(item, body) {
      const running = item.phAnimation;
      const opening = running ? !item.phOpening : !item.open;
      const style = getComputedStyle(body);
      const from = running ? style.height : (opening ? '0px' : `${body.scrollHeight}px`);
      const fromPad = running ? style.paddingBottom : (opening ? '0px' : this.padding(body));
      if (running) running.cancel();
      if (opening) item.open = true;
      // Read the target after the details is open, otherwise scrollHeight is the collapsed box.
      const to = opening ? `${body.scrollHeight}px` : '0px';
      const toPad = opening ? this.padding(body) : '0px';
      const animation = body.animate(
        [
          { height: from, paddingBottom: fromPad, opacity: opening ? 0 : 1 },
          { height: to, paddingBottom: toPad, opacity: opening ? 1 : 0 },
        ],
        { duration: 220, easing: 'cubic-bezier(.2,.7,.2,1)' }
      );
      item.phAnimation = animation;
      item.phOpening = opening;
      animation.finished
        .then(() => {
          if (item.phAnimation !== animation) return;
          item.phAnimation = null;
          // Set from the animation's own direction rather than assuming a close, so a
          // reversed click leaves the details matching what was actually animated.
          item.open = opening;
          // Drop the finished animation so the answer sizes from the stylesheet again
          // and a later reflow (font swap, resize) is not measured against stale values.
          animation.cancel();
        })
        .catch(() => {});
    }
    // The resting padding-bottom, read once at connect time before anything can animate, so a
    // running animation's inline value is never mistaken for the value the stylesheet sets.
    padding(body) {
      if (this.pad == null) this.pad = getComputedStyle(body).paddingBottom;
      return this.pad;
    }
  }
  // Review band: our "Write a review" buttons drive Judge.me's own form rather than
  // shipping a second one. Judge.me renders its trigger asynchronously, so the buttons
  // stay hidden until one exists and nothing dead is ever clickable.
  const JM_TRIGGER =
    '.jm-no-reviews-state__action-buttons .jm-button,.jm-action-buttons__button,' +
    '.jdgm-write-rev-link,.jm-write-review-btn,[data-jdgm-write-review],.jm-no-reviews-state .jm-btn';
  function phReviewTrigger(scope) {
    return (scope || document).querySelector(JM_TRIGGER);
  }
  function phSyncReviewButtons() {
    const buttons = document.querySelectorAll('[data-ph-write-review]');
    if (!buttons.length) return true;
    const band = document.querySelector('.ph-reviews');
    const ready = !!phReviewTrigger(band);
    buttons.forEach((button) => {
      button.hidden = !ready;
    });
    return ready;
  }
  function phInitReviews() {
    const band = document.querySelector('.ph-reviews');
    if (!band || !band.querySelector('[data-ph-write-review]')) return;
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-ph-write-review]');
      if (!button) return;
      const trigger = phReviewTrigger(document.querySelector('.ph-reviews'));
      if (!trigger) return;
      event.preventDefault();
      trigger.click();
      // Judge.me opens the form in place, so bring it into view for the shopper.
      requestAnimationFrame(() => {
        const form = document.querySelector('.jdgm-form-wrapper,.jdgm-form,.jm-review-form');
        (form || trigger).scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
    });
    // Judge.me injects late; watch the band until its trigger appears, then stop.
    if (phSyncReviewButtons()) return;
    const observer = new MutationObserver(() => {
      if (phSyncReviewButtons()) observer.disconnect();
    });
    observer.observe(band, { childList: true, subtree: true });
    // Give up watching after a while rather than observing for the life of the page.
    setTimeout(() => observer.disconnect(), 15000);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', phInitReviews);
  } else {
    phInitReviews();
  }

  if (!customElements.get('ph-policy')) customElements.define('ph-policy', PhPolicy);
  if (!customElements.get('ph-brands')) customElements.define('ph-brands', PhBrands);
  if (!customElements.get('ph-faq')) customElements.define('ph-faq', PhFaq);
  if (!customElements.get('ph-featured-tabs')) customElements.define('ph-featured-tabs', PhFeaturedTabs);
  if (!customElements.get('ph-header')) customElements.define('ph-header', PhHeader);
  if (!customElements.get('ph-sticky-atc')) customElements.define('ph-sticky-atc', PhStickyAtc);
  if (!customElements.get('ph-product-details')) customElements.define('ph-product-details', PhProductDetails);

  // Variant preselect: when a shopper picks an option value whose exact combination does not exist,
  // move the other options to the first existing (preferably available) variant that carries the
  // chosen value before Dawn handles the change. Capture phase runs ahead of Dawn's own listener;
  // Dawn then fetches and renders the variant natively. No pricing or form logic lives here.
  const phPreselectVariant = (event) => {
    const picker = event.target.closest('variant-selects');
    if (!picker) return;
    const control = event.target.closest('fieldset, .product-form__input--dropdown');
    if (!control) return;
    const data = picker.closest('product-info')?.querySelector('[data-ph-variants]');
    if (!data) return;
    let variants;
    try { variants = JSON.parse(data.textContent); } catch (e) { return; }
    if (!Array.isArray(variants) || !variants.length) return;
    const groups = Array.from(picker.querySelectorAll('fieldset, .product-form__input--dropdown'));
    const read = (group) => {
      const select = group.querySelector('select');
      if (select) return select.value;
      return group.querySelector('input:checked')?.value ?? '';
    };
    const selected = groups.map(read);
    const matches = (variant, values) => values.every((value, index) => variant.options[index] === value);
    if (variants.some((variant) => matches(variant, selected))) return;
    const changedIndex = groups.indexOf(control);
    if (changedIndex < 0) return;
    const candidates = variants.filter((variant) => variant.options[changedIndex] === selected[changedIndex]);
    const target = candidates.find((variant) => variant.available) || candidates[0];
    if (!target) return;
    groups.forEach((group, index) => {
      if (index === changedIndex) return;
      const value = target.options[index];
      const select = group.querySelector('select');
      if (select) {
        Array.from(select.options).forEach((option) => {
          if (option.value === value) option.setAttribute('selected', 'selected');
          else option.removeAttribute('selected');
        });
        select.value = value;
        return;
      }
      const input = Array.from(group.querySelectorAll('input')).find((candidate) => candidate.value === value);
      if (input) input.checked = true;
    });
  };
  document.addEventListener('change', phPreselectVariant, true);
})();
